import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import BaseSelect from 'teacher-ui-client/components/base/select/index.gts'
import UiButton from 'teacher-ui-client/components/ui-button/index.gts'
import StickerSelector from 'teacher-ui-client/components/writing-legends/sticker-selector.gts'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import LayoutBlocksConfirmModal from 'teacher-ui-client/components/layout-blocks/confirm-modal.gts'
import TeacherFeedbackCard from 'teacher-ui-client/components/writing-legends/teacher-feedback-card.gts'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { t, type IntlService } from 'ember-intl'
import TextArea from 'teacher-ui-client/components/base/text-area.gts'
import eventValue from 'teacher-ui-client/helpers/event-value'
import { not } from 'ember-truth-helpers'
import type WritingLegendsSubmissionService from 'teacher-ui-client/services/writing-legends-submission'
import { service } from '@ember/service'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import { task } from 'ember-concurrency'
import cuid from 'cuid'

interface Signature {
  Element: HTMLDivElement
  Args: {
    themeKey: ThemeKeys | undefined
    studentId?: string
    resultId?: string
    snapshotId?: string
    onFeedbackSuccess?: () => void
  }
}

export interface CreateFeedbackPayload {
  feedback_id: string
  student_id: string
  result_id: string
  activity_result_snapshot_id: string
  feedback_text: string
  mark: number
  sticker: string | null
}

export const MAX_MARK = 10

export class TeacherFeedbackForm extends Component<Signature> {
  @service writingLegendsSubmission!: WritingLegendsSubmissionService
  @service intl!: IntlService
  @service flashQueue!: FlashQueueService

  @tracked feedbackText = ''
  @tracked selectedMark: number = 0
  @tracked selectedSticker: string | null = null
  @tracked showSendFeedbackModal = false

  get markOptions() {
    return Array.from({ length: MAX_MARK }, (_, i) => i + 1)
  }

  submitFeedback = task({ drop: true }, async (url: string, feedback: CreateFeedbackPayload) => {
    const request = await this.writingLegendsSubmission.submitFeedback(url, feedback)

    if (request) {
      this.flashQueue.addSuccess({
        subtitle: this.intl.t('pages.recentWriting.viewWriting.feedbacks.sendFeedbackSuccess'),
      })

      // reset feedback form
      this.feedbackText = ''
      this.selectedMark = 0
      this.selectedSticker = null

      if (this.args.onFeedbackSuccess) {
        this.args.onFeedbackSuccess()
      }
    } else {
      this.flashQueue.addFail({ subtitle: this.intl.t('pages.recentWriting.viewWriting.feedbacks.sendFeedbackError') })
    }
  })

  sendFeedbackConfirm = async () => {
    if (!this.args.studentId || !this.args.resultId || !this.args.snapshotId) {
      return
    }

    const url = this.writingLegendsSubmission.buildFeedbackUrlForPost(
      this.args.studentId,
      this.args.resultId,
      this.args.snapshotId,
    )

    const feedback: CreateFeedbackPayload = {
      feedback_id: cuid(),
      student_id: this.args.studentId,
      result_id: this.args.resultId,
      activity_result_snapshot_id: this.args.snapshotId,
      feedback_text: this.feedbackText,
      mark: this.selectedMark,
      sticker: this.selectedSticker,
    }

    await this.submitFeedback.perform(url, feedback)

    this.showSendFeedbackModal = false
  }

  setFeedbackText = (value: string) => {
    this.feedbackText = value
  }

  setSelectedMark = (value: string) => {
    this.selectedMark = Number(value)
  }

  setSelectedSticker = (stickerName: string | null) => {
    this.selectedSticker = stickerName
  }

  get canSubmit() {
    return this.feedbackText.trim() !== '' && this.selectedMark > 0
  }

  get now() {
    return Date.now()
  }

  <template>
    <div class="border-2 border-[#b0edf5] bg-[#f1f9ff] p-2">
      <div>
        <TextArea
          @themeKey={{@themeKey}}
          rows="5"
          class="w-full border-0 bg-transparent focus:border-transparent focus:ring-0 focus:outline-none"
          @value={{this.feedbackText}}
          {{on "input" (eventValue this.setFeedbackText)}}
          placeholder="Type feedback here..."
        />
      </div>
      <div class="flex items-end justify-between">
        <div>
          <BaseSelect
            data-test-mark-select
            @themeKey={{@themeKey}}
            id="mark-select"
            name="mark-select"
            value={{this.selectedMark}}
            {{on "change" (eventValue this.setSelectedMark)}}
          >
            <option value="" disabled selected hidden>
              {{t "pages.recentWriting.viewWriting.feedbacks.selectMark"}}
            </option>
            {{#each this.markOptions as |mark|}}
              <option value={{mark}}>
                {{mark}}
              </option>
            {{/each}}
          </BaseSelect>
        </div>
        <div><StickerSelector @stickerName={{this.selectedSticker}} @onSelect={{this.setSelectedSticker}} /></div>
      </div>
    </div>
    <div class="my-4 flex justify-center">
      <UiButton
        data-test-send-feedback-button
        class="ml-4"
        @appearance={{ButtonAppearance.regular}}
        disabled={{not this.canSubmit}}
        {{on "click" (fn (mut this.showSendFeedbackModal) true)}}
      >{{t "pages.recentWriting.viewWriting.feedbacks.reviewAndSubmitFeedback"}}</UiButton>
    </div>

    <LayoutBlocksConfirmModal
      @title={{t "pages.recentWriting.viewWriting.feedbacks.confirmSendFeedbackTitle"}}
      @showModal={{this.showSendFeedbackModal}}
      @cancel={{fn (mut this.showSendFeedbackModal) false}}
      @confirm={{this.sendFeedbackConfirm}}
      @cancelButtonText={{t "cancel"}}
      @confirmButtonText={{t "pages.recentWriting.viewWriting.feedbacks.send"}}
      @confirmButtonAppearance={{ButtonAppearance.regular}}
      @isSubmitting={{this.submitFeedback.isRunning}}
    >
      <TeacherFeedbackCard
        @date={{this.now}}
        @title={{t "pages.recentWriting.viewWriting.feedbacks.yourFeedback"}}
        @mark={{this.selectedMark}}
        @sticker={{this.selectedSticker}}
        @disabled={{true}}
      >
        {{this.feedbackText}}
      </TeacherFeedbackCard>
    </LayoutBlocksConfirmModal>
  </template>
}

export default TeacherFeedbackForm
